import type { ApprovalDTO, ApprovalType, ApprovalStatus } from '../approval.types';
import dayjs from 'dayjs';
import i18next from 'i18next';
import { ApprovalStepModel } from './ApprovalStepModel';

export class ApprovalModel {
  private readonly _dto: ApprovalDTO;
  private readonly _steps: ApprovalStepModel[];

  constructor(dto: ApprovalDTO) {
    this._dto = dto;
    this._steps = (dto.steps || []).map(step => new ApprovalStepModel(step));
  }

  get originDTO(): ApprovalDTO {
    return this._dto;
  }

  get id(): string {
    return this._dto.id || '';
  }

  get type(): ApprovalType {
    return this._dto.type;
  }

  get title(): string {
    return this._dto.title || '';
  }

  get content(): string {
    return this._dto.content ?? '';
  }

  get status(): ApprovalStatus {
    return this._dto.status;
  }

  get tenantId(): string {
    return this._dto.tenantId || '';
  }

  get drafterId(): string {
    return this._dto.drafterId || '';
  }

  get drafter() {
    return this._dto.drafter;
  }

  get draftComment(): string {
    return this._dto.draftComment ?? '';
  }

  get steps(): ApprovalStepModel[] {
    return this._steps;
  }

  get createdDate(): string {
    return this._dto.createdDate ? dayjs(this._dto.createdDate).format('YYYY.MM.DD') : '';
  }

  get updatedDate(): string {
    return this._dto.updatedDate ? dayjs(this._dto.updatedDate).format('YYYY.MM.DD') : '';
  }

  get typeStr(): string {
    switch (this.type) {
      case 'GENERAL':
        return i18next.t('Approval.Type.General') || '일반';
      case 'SUBSCRIPTION_USE_REQUEST':
        return i18next.t('Approval.Type.Subscription_Use_Request') || '구독 사용신청';
      default:
        return '';
    }
  }

  get statusLabel(): string {
    switch (this.status) {
      case 'DRAFT':
        return i18next.t('Approval.Status.Draft') || '기안';
      case 'IN_PROGRESS':
        return i18next.t('Approval.Status.In_Progress') || '진행중';
      case 'APPROVED':
        return i18next.t('Approval.Status.Approved') || '승인';
      case 'REJECTED':
        return i18next.t('Approval.Status.Rejected') || '반려';
      case 'CANCELLED':
        return i18next.t('Approval.Status.Cancelled') || '취소';
      default:
        return '';
    }
  }

  get statusBackgroundColor(): string {
    switch (this.status) {
      case 'DRAFT':
        return 'bg-yellow';
      case 'IN_PROGRESS':
        return 'bg-pink';
      case 'APPROVED':
        return 'bg-green';
      case 'REJECTED':
        return 'bg-gray-dark';
      case 'CANCELLED':
        return 'bg-gray-dark';
      default:
        return 'bg-gray-dark';
    }
  }

  get statusTextColor(): string {
    switch (this.status) {
      case 'DRAFT':
        return 'text-gray-sub-darker';
      case 'IN_PROGRESS':
        return 'text-white';
      case 'APPROVED':
        return 'text-white';
      case 'REJECTED':
        return 'text-gray-sub-darker';
      case 'CANCELLED':
        return 'text-gray-sub-darker';
      default:
        return 'text-gray-sub-darker';
    }
  }

  get drafterName(): string {
    return this.drafter?.user?.name ?? '';
  }

  get drafterEmail(): string {
    return this.drafter?.user?.email ?? '';
  }

  get currentStep(): ApprovalStepModel | undefined {
    return this.steps.find(step => step.status === 'IN_PROGRESS');
  }

  get finalApproverId(): string {
    return this._dto.finalApproverId ?? '';
  }

  get finalApproverName(): string {
    return this._dto.finalApproverName ?? '';
  }

  get isCompleted(): boolean {
    return this.status === 'APPROVED' || this.status === 'REJECTED' || this.status === 'CANCELLED';
  }

  get targetId(): string {
    return this._dto.targetId ?? '';
  }
}
